import { useDorisApplyEntityFilters } from 'src/utils/Hooks/useDorisApplyEntityFilters';

export interface DorisBaseParams {
  metricType: string;
  fieldName?: string;
  retailerId?: number;
  categoryIds?: number[];
  subcategoryIds?: number[];
  brandIds?: number[];
  segmentIds?: string[];
  minRetailPrice?: number;
  maxRetailPrice?: number;
  resultCount?: number;
  businessunitids?: string[];
  keywords?: string[];
  topicIds?: string[];
  [key: string]: any;
}

/**
 * A utility function to build consistent Doris API payloads
 * Creates a standardized payload based on provided parameters and filters
 *
 * @param baseParams - Basic parameters for the Doris API call
 * @param additionalParams - Additional parameters to merge with the base params
 * @param applyEntityFilters - Function from useDorisApplyEntityFilters hook
 * @param retailerId - Retailer ID from Redux state
 * @param startWeekId - Start week ID (typically from time period state)
 * @param endWeekId - End week ID (typically from time period state)
 * @returns - Complete Doris API payload with all parameters and applied filters
 */
export const buildDorisPayload = (
  baseParams: DorisBaseParams,
  additionalParams: Record<string, any> = {},
  applyEntityFilters: ReturnType<typeof useDorisApplyEntityFilters>,
  retailerId: number,
  startWeekId?: number,
  endWeekId?: number
): Record<string, any> => {
  const paramsForFilter = {
    categoryIds: baseParams.categoryIds,
    subcategoryIds: baseParams.subcategoryIds,
    brandIds: baseParams.brandIds,
    segmentIds: baseParams.segmentIds,
    minRetailPrice: baseParams.minRetailPrice,
    maxRetailPrice: baseParams.maxRetailPrice,
    businessunitids: baseParams.businessunitids
  };

  // Apply entity filters to get filtered IDs
  const filteredParams = applyEntityFilters(paramsForFilter);

  // Build the final payload with all parameters
  return {
    ...baseParams,
    ...filteredParams,
    retailerId: Number(retailerId),
    startWeekId,
    endWeekId,
    ...additionalParams
  };
};

/**
 * A specialized version of buildDorisPayload that handles interceptor functions
 * Used for hooks that need to modify the payload before sending
 *
 * @param baseParams - Basic parameters for the Doris API call
 * @param additionalParams - Additional parameters including interceptors
 * @param applyEntityFilters - Function from useDorisApplyEntityFilters hook
 * @param retailerId - Retailer ID from Redux state
 * @param startWeekId - Start week ID (typically from time period state)
 * @param endWeekId - End week ID (typically from time period state)
 * @returns - Complete Doris API payload with all parameters and applied filters
 */
export const buildDorisPayloadWithInterceptors = (
  baseParams: DorisBaseParams,
  additionalParams: Record<string, any> = {},
  applyEntityFilters: ReturnType<typeof useDorisApplyEntityFilters>,
  retailerId: number,
  startWeekId?: number,
  endWeekId?: number
): Record<string, any> => {
  // Extract interceptor if it exists
  const { interceptBaseParams, ...restAdditionalParams } = additionalParams;

  // Build the standard payload first
  const standardPayload = buildDorisPayload(
    baseParams,
    restAdditionalParams,
    applyEntityFilters,
    retailerId,
    startWeekId,
    endWeekId
  );

  // Apply the interceptor if provided
  if (interceptBaseParams && typeof interceptBaseParams === 'function') {
    const interceptedParams = interceptBaseParams(standardPayload);
    return interceptedParams;
  }

  return standardPayload;
};
